import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { AuthState } from 'src/app/store/auth';

// Session storage key to track if user-initialization has completed
const USER_INIT_COMPLETE_KEY = 'blume_user_init_complete';

/**
 * Auth Guard
 *
 * Protects routes by checking if the user is authenticated and if their
 * data has been initialized. If authenticated but data not loaded,
 * redirects to the loading page to initialize user data.
 *
 * @example
 * // In app.routes.ts
 * {
 *   path: 'protected',
 *   canActivate: [authGuard],
 *   loadComponent: () => import('./protected.page').then(m => m.ProtectedPage)
 * }
 *
 * @returns true if authenticated and initialized, false otherwise (with redirect)
 */
export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const store = inject(Store);
  const router = inject(Router);

  // Get the target URL from the router's current navigation
  const navigation = router.getCurrentNavigation();
  const fullUrl = navigation?.finalUrl?.toString() || router.url;

  console.log('[AuthGuard] Checking route:', fullUrl);

  // Don't check initialization for the loading page or setup-profile
  // (setup-profile is for users who are authenticated but have no Firestore document yet)
  if (fullUrl.startsWith('/loading') || fullUrl.startsWith('/setup-profile')) {
    console.log('[AuthGuard] Loading/setup page, allowing access');
    return true;
  }

  // Check if user is authenticated
  const isAuthenticated = store.selectSnapshot(AuthState.isAuthenticated);

  if (!isAuthenticated) {
    console.log('[AuthGuard] User not authenticated, redirecting to sign-in');
    // Clear stale session flag
    sessionStorage.removeItem(USER_INIT_COMPLETE_KEY);
    
    // Redirect to sign-in with the originally requested URL
    const returnUrl = fullUrl || '/account';
    
    router.navigate(['/sign-in'], {
      queryParams: { returnUrl }
    });
    return false;
  }

  // Check if user-initialization has completed using sessionStorage flag
  // Note: This flag is cleared on app initialization (app.component.ts)
  // so it only exists during the current session after /loading completes
  const userInitComplete = sessionStorage.getItem(USER_INIT_COMPLETE_KEY);

  if (!userInitComplete) {
    // No flag - initialization hasn't run yet in this session
    // Redirect to loading page immediately
    const returnUrl = fullUrl === '/' ? '/account' : fullUrl;
    
    console.log('[AuthGuard] No init flag, redirecting to loading with returnUrl:', returnUrl);
    
    router.navigate(['/loading'], {
      queryParams: { returnUrl }
    });
    return false;
  }

  // Flag exists - initialization has completed in this session
  console.log('[AuthGuard] Init complete, access granted to:', fullUrl);
  return true;
};

/**
 * Guest Guard
 *
 * Protects routes that should only be accessible to non-authenticated users.
 * If the user is authenticated, redirects to the loading page.
 *
 * Useful for sign-in and sign-up pages to prevent authenticated users
 * from accessing them.
 *
 * @example
 * // In app.routes.ts
 * {
 *   path: 'sign-in',
 *   canActivate: [guestGuard],
 *   loadComponent: () => import('./sign-in.page').then(m => m.SignInPage)
 * }
 *
 * @returns true if not authenticated, false otherwise (with redirect)
 */
export const guestGuard: CanActivateFn = (route) => {
  const store = inject(Store);
  const router = inject(Router);

  console.log('[GuestGuard] Checking route, authenticated check...');

  // Check if user is authenticated
  const isAuthenticated = store.selectSnapshot(AuthState.isAuthenticated);

  if (isAuthenticated) {
    // Preserve returnUrl from route query params (e.g., /sign-in?returnUrl=/students)
    const returnUrl = route.queryParams['returnUrl'] || '/workspace/home';
    
    // Check if initialization flag exists
    const userInitComplete = sessionStorage.getItem(USER_INIT_COMPLETE_KEY);
    
    if (userInitComplete) {
      // Already initialized - redirect directly to destination
      console.log('[GuestGuard] Authenticated + initialized, redirecting to:', returnUrl);
      router.navigateByUrl(returnUrl, { replaceUrl: true });
    } else {
      // Not initialized - redirect to loading page
      console.log('[GuestGuard] Authenticated but not initialized, redirecting to /loading with returnUrl:', returnUrl);
      router.navigate(['/loading'], {
        queryParams: { returnUrl },
        replaceUrl: true
      });
    }
    return false;
  }

  console.log('[GuestGuard] Not authenticated, allowing access');
  return true;
};
